/* KPI row + Verdict card — extracted from BulletinComplianceOverview.js
 * (iter83). Kept generic-typed props so testids remain intact. */
import { Card, CardContent } from '../ui/card';
import { TrendUp, Warning } from '@phosphor-icons/react';

const fmtMonth = (m) => {
  if (!m) return '—';
  const [y, mo] = m.split('-');
  const dt = new Date(parseInt(y, 10), parseInt(mo, 10) - 1, 1);
  return dt.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
};

export const BulletinKpiRow = ({ kpi }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
    <Card>
      <CardContent className="p-4">
        <p className="text-[10px] uppercase tracking-wide text-[#64748B] font-bold">
          Taux moyen de signature
        </p>
        <p className="text-3xl font-outfit font-bold text-[#0052FF]"
          data-testid="bul-kpi-avg-rate">
          {kpi.avg_signature_rate}%
        </p>
        <p className="text-[10px] text-[#94A3B8]">sur {kpi.months_analyzed} mois</p>
      </CardContent>
    </Card>
    <Card>
      <CardContent className="p-4">
        <p className="text-[10px] uppercase tracking-wide text-[#64748B] font-bold">
          Bulletins émis (cumul)
        </p>
        <p className="text-3xl font-outfit font-bold text-emerald-700"
          data-testid="bul-kpi-total">
          {kpi.total_bulletins}
        </p>
        <p className="text-[10px] text-[#94A3B8]">sur la période</p>
      </CardContent>
    </Card>
    <Card>
      <CardContent className="p-4">
        <p className="text-[10px] uppercase tracking-wide text-emerald-700 font-bold flex items-center gap-1">
          <TrendUp size={12} weight="bold" /> Meilleur mois
        </p>
        <p className="text-2xl font-outfit font-bold text-emerald-700"
          data-testid="bul-kpi-best-month">
          {fmtMonth(kpi.best_month)}
        </p>
        <p className="text-[10px] text-emerald-700/70">{kpi.best_rate}% signés</p>
      </CardContent>
    </Card>
    <Card>
      <CardContent className="p-4">
        <p className="text-[10px] uppercase tracking-wide text-red-700 font-bold flex items-center gap-1">
          <Warning size={12} weight="bold" /> Pire mois
        </p>
        <p className="text-2xl font-outfit font-bold text-red-700"
          data-testid="bul-kpi-worst-month">
          {fmtMonth(kpi.worst_month)}
        </p>
        <p className="text-[10px] text-red-700/70">{kpi.worst_rate}% signés</p>
      </CardContent>
    </Card>
  </div>
);

export const BulletinVerdictCard = ({ forecast, recurringCount }) => {
  if (!forecast || forecast.length === 0) {
    return (
      <div
        data-testid="bul-verdict-card-empty"
        className="rounded-xl p-5 border-2 border-dashed border-[#E2E8F0] bg-[#F8FAFC] text-center"
      >
        <p className="text-sm text-[#64748B]">
          Prévision indisponible — historique insuffisant (au moins 2 mois
          d'activité requis pour la régression linéaire).
        </p>
      </div>
    );
  }
  const nextRate = forecast[0]?.predicted_rate ?? 0;
  const recCount = recurringCount ?? 0;
  let verdict, color, bg, border, msg, icon;
  if (nextRate >= 80 && recCount <= 2) {
    verdict = 'En bonne voie';
    color = '#059669'; bg = '#ECFDF5'; border = '#A7F3D0';
    icon = '🟢';
    msg = `Prévision ${nextRate}% pour ${fmtMonth(forecast[0].month)} — continuez comme ça.`;
  } else if (nextRate >= 50 && recCount <= 5) {
    verdict = 'Risque modéré';
    color = '#B45309'; bg = '#FFFBEB'; border = '#FCD34D';
    icon = '🟡';
    msg = `Prévision ${nextRate}% + ${recCount} élève(s) récurrent(s). Anticipez la relance.`;
  } else {
    verdict = 'Action requise';
    color = '#B91C1C'; bg = '#FEF2F2'; border = '#FECACA';
    icon = '🔴';
    msg = `Prévision ${nextRate}% + ${recCount} récurrent(s). Lancez une campagne de relance immédiate.`;
  }
  return (
    <div
      data-testid="bul-verdict-card"
      data-verdict={verdict}
      style={{ background: bg, border: `2px solid ${border}` }}
      className="rounded-xl p-5 flex items-start gap-4"
    >
      <div className="text-4xl leading-none" aria-hidden="true">{icon}</div>
      <div className="flex-1">
        <p className="text-[10px] uppercase tracking-wide font-bold" style={{ color }}>
          Prévision fin de mois
        </p>
        <h2 className="text-2xl font-outfit font-bold mt-1" style={{ color }}>{verdict}</h2>
        <p className="text-sm mt-1" style={{ color: color + 'CC' }}>{msg}</p>
      </div>
      <div className="text-right">
        <p className="text-[10px] uppercase tracking-wide text-[#64748B]">
          {fmtMonth(forecast[0].month)}
        </p>
        <p className="text-3xl font-outfit font-bold" style={{ color }}
          data-testid="bul-verdict-predicted-rate">
          {nextRate}%
        </p>
      </div>
    </div>
  );
};
